import React from 'react';
import styled from 'styled-components';
import { SkeletonButton } from '@ui/buttons';

// A chip: a SkeletonButton used as a toggle, at chip size.
//
// Chips are the most redesign-prone element on this page and there was no chip component to
// redesign. The sub-tree was written four times — identically in IndexFilter and ColumnChooser, and
// in two more variants in TableControls and FeedViewForm, where the wrapper is called `MockRow` so a
// grep for the pattern misses it. A new chip size, radius or selected treatment was a four-file
// edit.
//
// THE OPACITY DIMMING IS GONE, and that is the one visible change here. Every one of those wrappers
// dimmed the unpressed chip (0.5, or 0.6 in FeedViewForm) while SkeletonButton ALREADY paints
// `aria-pressed='true'` as a filled inversion — so the off state was double-encoded and a designer
// changing the selected treatment got two competing signals and had to find both. Off is now the
// outline treatment at full strength, which is what SkeletonButton's own comment says it is for.
//
// The descendant `button { padding }` idiom is inherited rather than invented — BaseSkeletonButtonWrapper
// uses it internally — and it is still fragile: the moment SkeletonButton wraps its label one element
// deeper, every chip here reverts to the 12px/24px default. A `size` prop on SkeletonButton itself is
// the real fix, and is a change to a shared component rather than to this feature.
const ChipWrap = styled.div`
  button {
    padding: 6px 14px;
  }

  /* The chooser's third state: a column switched on whose values this read did not bring back. Not
     pressed, not plain — pending, and dashed says so without inventing a colour. */
  ${({ $pending }) => ($pending ? 'button { border-style: dashed; }' : '')}
`;

// A row of them. CompanyFilter already declared a ChipRow for its company chips, which is why this
// carries the same name: one row-of-chips idiom, not two.
export const ChipRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  /* Chip padding here as well as on the chip itself, deliberately: a chip row also holds buttons
     that are not toggles — IndexFilter's "Clear indices" — and every button in a row of chips has to
     be the same size whether it is one or not. This is the rule the row it replaces already had. */
  button {
    padding: 6px 14px;
  }
`;

export const FilterChip = ({ children, pressed = false, pending = false, label, title, onPress }) => (
  <ChipWrap $pending={pending}>
    <SkeletonButton
      onClick={onPress}
      buttonProps={{
        'aria-pressed': pressed,
        ...(label ? { 'aria-label': label } : {}),
        ...(title ? { title } : {}),
      }}
    >
      {children}
    </SkeletonButton>
  </ChipWrap>
);
